import { useLazyQuery, useMutation } from "@apollo/client";
import { Alert, Box, Button, Card, Chip, Stack, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import { SHARE_EVENT_PREVIEW, SHARE_EVENT_TO_EMAILS } from "../gql/queries/shareEvent";
import { Spacing } from "../types/enum";

/**
 * Pull the event id out of whatever was pasted — a full app.heylady.io/event/…
 * link, or the bare id on its own.
 */
const extractEventId = (input: string): string => {
  const trimmed = input.trim();
  const match = trimmed.match(/event\/([0-9a-fA-F-]{36})/);
  return match ? match[1] : trimmed;
};

/** Split a pasted blob into emails, however it was separated. */
const parseEmails = (input: string): string[] =>
  input
    .split(/[\s,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);

const ShareEventPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [eventInput, setEventInput] = useState("");
  const [emailsInput, setEmailsInput] = useState("");

  const emails = parseEmails(emailsInput);
  const eventId = extractEventId(eventInput);

  const [runPreview, { data: previewData, loading: previewLoading }] =
    useLazyQuery(SHARE_EVENT_PREVIEW);
  const [shareEvent, { loading: shareLoading }] = useMutation(SHARE_EVENT_TO_EMAILS);

  const preview = previewData?.adminShareEventPreview;

  const onPreview = () => {
    if (emails.length === 0) {
      enqueueSnackbar("Paste some emails first.", { variant: "info" });
      return;
    }
    // Pass the event when we have one, so the preview can also say who's allowed.
    void runPreview({ variables: { emails, eventId: eventId || null } });
  };

  const onShare = async () => {
    if (!eventId) {
      enqueueSnackbar("Paste the event link or id first.", { variant: "info" });
      return;
    }
    if (emails.length === 0) {
      enqueueSnackbar("Paste some emails first.", { variant: "info" });
      return;
    }
    try {
      const { data } = await shareEvent({
        variables: { eventId, emails, source: "SHARE_EVENT" },
      });
      const result = data?.adminShareEventToEmails;
      enqueueSnackbar(
        `Shared with ${result?.sharedCount ?? 0} member${result?.sharedCount === 1 ? "" : "s"}.` +
          (result?.notFound?.length
            ? ` ${result.notFound.length} email(s) matched no member.`
            : ""),
        { variant: "success" },
      );
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Share failed.", {
        variant: "error",
      });
    }
  };

  return (
    <>
      <PageHeader title="Share Event">
        Send a "shared this event with you" heads-up to a list of members — the same as the Share
        button, but for many at once. Paste the event link and the emails, preview who was found,
        then share.
      </PageHeader>

      <Card>
        <Box p={Spacing.l}>
          <Stack spacing={Spacing.m}>
            <TextField
              label="Event link or id"
              placeholder="https://app.heylady.io/event/… or the id"
              value={eventInput}
              onChange={(event) => setEventInput(event.target.value)}
              fullWidth
            />

            <TextField
              label="Emails"
              placeholder="Paste emails — commas, spaces or new lines all work"
              value={emailsInput}
              onChange={(event) => setEmailsInput(event.target.value)}
              helperText={emails.length > 0 ? `${emails.length} email(s) pasted` : " "}
              multiline
              minRows={5}
              fullWidth
            />

            {preview ? (
              <Alert severity={preview.eligibleCount === 0 ? "warning" : "info"}>
                <Typography variant="body2" gutterBottom>
                  {typeof preview.eligibleCount === "number" ? (
                    <>
                      <strong>{preview.eligibleCount}</strong> member
                      {preview.eligibleCount === 1 ? "" : "s"} will receive this event.
                    </>
                  ) : (
                    <>
                      <strong>{preview.matchedCount}</strong> of {preview.totalPasted} emails
                      matched a member. Add the event above to see who's allowed in it.
                    </>
                  )}
                </Typography>

                <Stack spacing={0.5} mt={Spacing.s}>
                  {!!preview.alreadySharedCount && (
                    <Typography variant="body2" color="textSecondary">
                      {preview.alreadySharedCount} already had this event shared with them (they'll
                      be skipped).
                    </Typography>
                  )}
                  {!!preview.inactiveCount && (
                    <Typography variant="body2" color="textSecondary">
                      {preview.inactiveCount} are inactive members.
                    </Typography>
                  )}
                  {!!preview.notAllowedEmails?.length && (
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {preview.notAllowedEmails.length} aren't allowed in this event (their plan
                        or verification doesn't cover it):
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {preview.notAllowedEmails.map((email: string) => (
                          <Chip key={email} label={email} size="small" color="warning" />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {preview.notFound.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {preview.notFound.length} matched no member (check the address):
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {preview.notFound.map((email: string) => (
                          <Chip key={email} label={email} size="small" color="error" />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Alert>
            ) : null}

            <Box display="flex" gap={Spacing.s}>
              <Button variant="outlined" onClick={onPreview} disabled={previewLoading}>
                Preview
              </Button>
              <Button variant="contained" onClick={onShare} disabled={shareLoading}>
                Share event
              </Button>
            </Box>
          </Stack>
        </Box>
      </Card>
    </>
  );
};

export default ShareEventPage;
